import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import { MdClear } from "react-icons/md";

type FieldName = "bedsMin" | "bedsMax" | "bathsMin" | "bathsMax";

interface BedBathValues {
    bedsMin: string | number;
    bedsMax: string | number;
    bathsMin: string | number;
    bathsMax: string | number;
}

interface BedBathInputProps {
    values?: BedBathValues;
    onChange: (field: FieldName, value: string | number) => void;
}

const BedBathInput: React.FC<BedBathInputProps> = ({
    values = { bedsMin: "", bedsMax: "", bathsMin: "", bathsMax: "" },
    onChange,
}) => {
    const [touchedBedsMin, setTouchedBedsMin] = useState(false);
    const [touchedBedsMax, setTouchedBedsMax] = useState(false);
    const [touchedBathsMin, setTouchedBathsMin] = useState(false);
    const [touchedBathsMax, setTouchedBathsMax] = useState(false);
    const [focusedField, setFocusedField] = useState<FieldName | null>(null);

    const asNumber = (val: string | number): number => (val === "" ? 0 : Number(val));

    const inc = (field: FieldName) => {
        const currentValue = asNumber(values[field]);
        onChange(field, (currentValue + 1).toString());
        handleBlur(field);
    };

    const dec = (field: FieldName) => {
        const currentValue = asNumber(values[field]);
        if (currentValue > 0) {
            onChange(field, (currentValue - 1).toString());
            handleBlur(field);
        }
    };

    const handleFocus = (field: FieldName) => {
        setFocusedField(field);
    };

    const handleBlur = (field: FieldName) => {
        setFocusedField(null);
        switch (field) {
            case "bedsMin":
                setTouchedBedsMin(true);
                setTouchedBedsMax(true);
                break;
            case "bedsMax":
                setTouchedBedsMax(true);
                setTouchedBedsMin(true);
                break;
            case "bathsMin":
                setTouchedBathsMin(true);
                setTouchedBathsMax(true);
                break;
            case "bathsMax":
                setTouchedBathsMax(true);
                setTouchedBathsMin(true);
                break;
            default:
                break;
        }
    };

    const isBedsInvalid =
        (touchedBedsMin || touchedBedsMax) &&
        focusedField === null &&
        values.bedsMax !== "" &&
        values.bedsMin !== "" &&
        Number(values.bedsMax) < Number(values.bedsMin);

    const isBathsInvalid =
        (touchedBathsMin || touchedBathsMax) &&
        focusedField === null &&
        values.bathsMax !== "" &&
        values.bathsMin !== "" &&
        Number(values.bathsMax) < Number(values.bathsMin);

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* Beds */}
            <div className="tw-flex-1">
                <label className="tw-block tw-mb-1 tw-text-[14px] tw-text-[#555E67]">Beds</label>
                <div className="tw-flex tw-gap-2">
                    {/* Min Field */}
                    <div className="tw-flex-1">
                        <div className="tw-flex tw-gap-1 tw-items-center">
                            <TextField
                                id="feed-beds-min"
                                type="number"
                                value={values.bedsMin}
                                onChange={(e) =>
                                    onChange("bedsMin", e.target.value === "" ? "" : Number(e.target.value))
                                }
                                onFocus={() => handleFocus("bedsMin")}
                                onBlur={() => handleBlur("bedsMin")}
                                size="small"
                                fullWidth
                                error={isBedsInvalid}
                                InputProps={{
                                    className: "!tw-px-1 tw-text-center",
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                onClick={() => dec("bedsMin")}
                                                edge="start"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlineMinus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => inc("bedsMin")}
                                                edge="end"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlinePlus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        textAlign: "center",
                                        "&.Mui-error": {
                                            color: "red",
                                        },
                                    },
                                    inputProps: {
                                        style: {
                                            textAlign: "center",
                                            color: isBedsInvalid ? "red" : "inherit",
                                        },
                                        min: 0,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => onChange("bedsMin", "")}
                                disabled={values.bedsMin === ""}
                                sx={{
                                    padding: "4px",
                                    width: 24,
                                    height: 24,
                                    opacity: values.bedsMin !== "" ? 1 : 0.3,
                                }}
                            >
                                <MdClear />
                            </IconButton>
                        </div>
                        <span className="tw-text-xs tw-text-gray-400">min</span>
                    </div>

                    {/* Max Field */}
                    <div className="tw-flex-1">
                        <div className="tw-flex tw-gap-1 tw-items-center">
                            <TextField
                                id="feed-beds-max"
                                type="number"
                                value={values.bedsMax}
                                onChange={(e) =>
                                    onChange("bedsMax", e.target.value === "" ? "" : Number(e.target.value))
                                }
                                onFocus={() => handleFocus("bedsMax")}
                                onBlur={() => handleBlur("bedsMax")}
                                size="small"
                                fullWidth
                                error={isBedsInvalid}
                                InputProps={{
                                    className: "!tw-px-1 tw-text-center",
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                onClick={() => dec("bedsMax")}
                                                edge="start"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlineMinus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => inc("bedsMax")}
                                                edge="end"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlinePlus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        textAlign: "center",
                                        "&.Mui-error": {
                                            color: "red",
                                        },
                                    },
                                    inputProps: {
                                        style: {
                                            textAlign: "center",
                                            color: isBedsInvalid ? "red" : "inherit",
                                        },
                                        min: 0,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => onChange("bedsMax", "")}
                                disabled={values.bedsMax === ""}
                                sx={{
                                    padding: "4px",
                                    width: 24,
                                    height: 24,
                                    opacity: values.bedsMax !== "" ? 1 : 0.3,
                                }}
                            >
                                <MdClear />
                            </IconButton>
                        </div>
                        <div className="tw-flex tw-gap-1 tw-items-center">
                            <div className="tw-flex-1"></div>
                            <span className="tw-text-xs tw-text-gray-400">max</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Baths */}
            <div className="tw-flex-1">
                <label className="tw-block tw-mb-1 tw-text-[14px] tw-text-[#555E67]">Baths</label>
                <div className="tw-flex tw-gap-2">
                    {/* Min Field */}
                    <div className="tw-flex-1">
                        <div className="tw-flex tw-gap-1 tw-items-center">
                            <TextField
                                id="feed-baths-min"
                                type="number"
                                value={values.bathsMin}
                                onChange={(e) =>
                                    onChange("bathsMin", e.target.value === "" ? "" : Number(e.target.value))
                                }
                                onFocus={() => handleFocus("bathsMin")}
                                onBlur={() => handleBlur("bathsMin")}
                                size="small"
                                fullWidth
                                error={isBathsInvalid}
                                InputProps={{
                                    className: "!tw-px-1 tw-text-center",
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                onClick={() => dec("bathsMin")}
                                                edge="start"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlineMinus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => inc("bathsMin")}
                                                edge="end"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlinePlus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        textAlign: "center",
                                        "&.Mui-error": {
                                            color: "red",
                                        },
                                    },
                                    inputProps: {
                                        style: {
                                            textAlign: "center",
                                            color: isBathsInvalid ? "red" : "inherit",
                                        },
                                        min: 0,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => onChange("bathsMin", "")}
                                disabled={values.bathsMin === ""}
                                sx={{
                                    padding: "4px",
                                    width: 24,
                                    height: 24,
                                    opacity: values.bathsMin !== "" ? 1 : 0.3,
                                }}
                            >
                                <MdClear />
                            </IconButton>
                        </div>
                        <span className="tw-text-xs tw-text-gray-400">min</span>
                    </div>

                    {/* Max Field */}
                    <div className="tw-flex-1">
                        <div className="tw-flex tw-gap-1 tw-items-center">
                            <TextField
                                id="feed-baths-max"
                                type="number"
                                value={values.bathsMax}
                                onChange={(e) =>
                                    onChange("bathsMax", e.target.value === "" ? "" : Number(e.target.value))
                                }
                                onFocus={() => handleFocus("bathsMax")}
                                onBlur={() => handleBlur("bathsMax")}
                                size="small"
                                fullWidth
                                error={isBathsInvalid}
                                InputProps={{
                                    className: "!tw-px-1 tw-text-center",
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                onClick={() => dec("bathsMax")}
                                                edge="start"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlineMinus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => inc("bathsMax")}
                                                edge="end"
                                                sx={{
                                                    padding: "4px",
                                                    backgroundColor: "#EFEFEF",
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 0,
                                                }}
                                            >
                                                <AiOutlinePlus />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        textAlign: "center",
                                        "&.Mui-error": {
                                            color: "red",
                                        },
                                    },
                                    inputProps: {
                                        style: {
                                            textAlign: "center",
                                            color: isBathsInvalid ? "red" : "inherit",
                                        },
                                        min: 0,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => onChange("bathsMax", "")}
                                disabled={values.bathsMax === ""}
                                sx={{
                                    padding: "4px",
                                    width: 24,
                                    height: 24,
                                    opacity: values.bathsMax !== "" ? 1 : 0.3,
                                }}
                            >
                                <MdClear />
                            </IconButton>
                        </div>
                        <div className="tw-flex tw-gap-1 tw-items-center">
                            <div className="tw-flex-1"></div>
                            <span className="tw-text-xs tw-text-gray-400">max</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BedBathInput;
